/**
 * Payment-intents list (refreshed).
 *
 * URL is the source of truth for filters so the page is shareable +
 * refresh-stable. Search debounces 250ms before syncing to the URL.
 * Row click opens a quick-look Sheet; "Open full page" navigates to
 * the canonical detail.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Inbox, RotateCw } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/shell/DataTable";
import { EmptyState } from "@/components/shell/EmptyState";
import { FilterChip } from "@/components/shell/FilterChip";
import { PageHeader } from "@/components/shell/PageHeader";
import { SearchInput } from "@/components/shell/SearchInput";
import { Toolbar, FilterGroup } from "@/components/shell/Toolbar";
import { PaymentIntentSheet } from "@/components/payments/PaymentIntentSheet";
import {
  getPaymentIntents,
  type IntentDirection,
  type IntentState,
  type PaymentIntentRow,
  type TenantEnvironment,
} from "@/lib/api";
import { useAuth } from "@/lib/use-auth";
import { formatMinorUnits } from "@/lib/format";
import { INTENT_STATE_BADGE_VARIANT } from "@/lib/state-badges";

const ENVIRONMENTS: readonly TenantEnvironment[] = ["live", "test"] as const;
const STATES: readonly IntentState[] = [
  "requires_action",
  "processing",
  "succeeded",
  "failed",
  "cancelled",
  "refunding",
  "refunded",
] as const;
const DIRECTIONS: readonly IntentDirection[] = ["collect", "payout", "refund"] as const;

export function PaymentIntentsListPage() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();

  const env = (params.get("environment") as TenantEnvironment | null) ?? undefined;
  const status = (params.get("status") as IntentState | null) ?? undefined;
  const direction = (params.get("direction") as IntentDirection | null) ?? undefined;
  const q = params.get("q") ?? undefined;

  const [searchInput, setSearchInput] = useState(q ?? "");
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (searchInput) next.set("q", searchInput);
      else next.delete("q");
      setParams(next, { replace: true });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const query = useQuery({
    queryKey: ["payment-intents", { env, status, direction, q }] as const,
    queryFn: () => getPaymentIntents({ environment: env, status, direction, q }),
  });

  useEffect(() => {
    if (query.error) {
      toast.error("Couldn't load payment intents", {
        description: "Check your network and try again.",
      });
    }
  }, [query.error]);

  const [sheetId, setSheetId] = useState<string | null>(null);

  function setFilter(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }

  const hasFilters = Boolean(env || status || direction || q);
  const isZeroGrantTenant =
    user?.role === "tenant" && user.tenants.length === 0 && !hasFilters;

  const columns = useMemo<ReadonlyArray<DataTableColumn<PaymentIntentRow>>>(
    () => [
      {
        key: "id",
        header: "Intent",
        cell: (r) => (
          <code className="font-mono text-xs text-muted-foreground">{r.id.slice(0, 14)}…</code>
        ),
      },
      {
        key: "tenant",
        header: "Tenant",
        cell: (r) => (
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate font-medium">{r.tenant_name}</span>
            <Badge variant="muted" className="capitalize">
              {r.tenant_environment}
            </Badge>
          </div>
        ),
      },
      {
        key: "direction",
        header: "Direction",
        cell: (r) => (
          <span className="text-sm capitalize text-muted-foreground">
            {r.direction} · {r.method}
          </span>
        ),
      },
      {
        key: "amount",
        header: "Amount",
        className: "text-right",
        cell: (r) => (
          <span className="font-mono tabular-nums">
            {formatMinorUnits(r.amount, r.currency)}
          </span>
        ),
      },
      {
        key: "state",
        header: "State",
        cell: (r) => (
          <Badge variant={INTENT_STATE_BADGE_VARIANT[r.state]} className="capitalize">
            {r.state.replace(/_/g, " ")}
          </Badge>
        ),
      },
      {
        key: "created_at",
        header: "Created",
        className: "text-muted-foreground",
        cell: (r) => (
          <span className="whitespace-nowrap text-xs">
            {new Date(r.created_at).toLocaleString()}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        eyebrow="Money / Outbound"
        title="Payment intents"
        description="Collects, payouts, and refunds across every tenant you can see."
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RotateCw className={query.isFetching ? "animate-spin" : undefined} />
            Refresh
          </Button>
        }
      />

      <Toolbar
        search={
          <SearchInput
            placeholder="Search by intent ID, idempotency key, or provider ref…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onClear={() => setSearchInput("")}
          />
        }
        filters={
          <>
            <FilterGroup label="Env">
              <FilterChip active={env === undefined} onClick={() => setFilter("environment", null)}>
                All
              </FilterChip>
              {ENVIRONMENTS.map((e) => (
                <FilterChip key={e} active={env === e} onClick={() => setFilter("environment", e)}>
                  {e}
                </FilterChip>
              ))}
            </FilterGroup>
            <FilterGroup label="Direction">
              <FilterChip
                active={direction === undefined}
                onClick={() => setFilter("direction", null)}
              >
                All
              </FilterChip>
              {DIRECTIONS.map((d) => (
                <FilterChip key={d} active={direction === d} onClick={() => setFilter("direction", d)}>
                  {d}
                </FilterChip>
              ))}
            </FilterGroup>
            <FilterGroup label="State">
              <FilterChip active={status === undefined} onClick={() => setFilter("status", null)}>
                All
              </FilterChip>
              {STATES.map((s) => (
                <FilterChip key={s} active={status === s} onClick={() => setFilter("status", s)}>
                  {s.replace(/_/g, " ")}
                </FilterChip>
              ))}
            </FilterGroup>
          </>
        }
      />

      <DataTable<PaymentIntentRow>
        columns={columns}
        rows={query.data?.results ?? []}
        loading={query.isLoading}
        rowKey={(r) => r.id}
        selectedKey={sheetId ?? undefined}
        onRowClick={(r) => setSheetId(r.id)}
        emptyState={
          isZeroGrantTenant ? (
            <EmptyState
              icon={Inbox}
              title="No tenants granted yet"
              description="Payment intents appear here once a super-admin grants you access to a tenant."
            />
          ) : (
            <EmptyState
              icon={Inbox}
              title="No payment intents match these filters"
              description="Try clearing a filter. New intents flow in here as they're created via the public API."
              action={
                hasFilters ? (
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/payment-intents">Clear filters</Link>
                  </Button>
                ) : undefined
              }
            />
          )
        }
      />

      <PaymentIntentSheet
        id={sheetId}
        open={sheetId !== null}
        onOpenChange={(open) => {
          if (!open) setSheetId(null);
        }}
      />
    </>
  );
}

export default PaymentIntentsListPage;
